import { useTranslation } from 'react-i18next'
import { Input } from '~/components/ui/input'
import { cn } from '~/lib/utils'

/**
 * Barre de filtres et d'actions posée au-dessus d'un tableau du back-office.
 *
 * Elle existe pour une raison de mise en page, pas de factorisation : cette
 * ligne portait `flex` sans `flex-wrap`, donc elle débordait dès que la fenêtre
 * se resserrait — et comme le tableau lui-même défile (`ui/table.tsx` pose son
 * `overflow-x-auto`), le débordement de la barre était le seul à ne pas se
 * rattraper. Sur un portable 13 pouces avec la barre latérale ouverte, le
 * bouton d'export finissait hors de l'écran.
 *
 * ⚠️ **Tous les contrôles sont des enfants directs**, filtres et actions dans
 * le même flux. Les grouper dans deux conteneurs — filtres à gauche, actions à
 * droite — était le réflexe naturel et donnait un résultat faux : le groupe de
 * filtres se repliait sur deux lignes *à l'intérieur de lui-même* pendant que
 * le groupe d'actions restait un bloc insécable, centré verticalement en face
 * du bloc de filtres, aligné sur aucune de ses deux lignes. Un seul flux de
 * repli fait descendre les actions à la suite du dernier filtre, ce qui est le
 * seul comportement lisible.
 */
export function TableToolbar({ children, className }: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {children}
    </div>
  )
}

/**
 * Actions de l'écran (ajouter, importer, exporter), à placer en dernier.
 *
 * `ml-auto` et non un `justify-between` sur le parent : sur une barre qui se
 * replie, `justify-between` écarte les contrôles de chaque ligne jusqu'aux deux
 * bords, ce qui disloque les filtres. La marge automatique ne pousse que ce
 * groupe-ci vers la droite, qu'il partage sa ligne avec les filtres ou qu'il
 * soit descendu seul.
 */
export function ToolbarActions({ children }: { children: React.ReactNode }) {
  return (
    <div className="ml-auto flex flex-wrap items-center gap-2">
      {children}
    </div>
  )
}

/**
 * `<select>` natif de filtre, au gabarit des champs de la barre.
 *
 * ⚠️ `max-w-[14rem]` n'est pas cosmétique : la largeur d'un `<select>` natif
 * est dictée par son option la plus longue, et un nom de parcours ou de groupe
 * saisi par l'admin n'a pas de longueur maximale. Sans plafond, un seul
 * parcours au nom bavard suffisait à faire déborder toute la barre — le défaut
 * ne se voyait donc qu'après la mise en données, jamais en développement.
 */
export function FilterSelect({ className, ...props }: React.ComponentProps<'select'>) {
  return (
    <select
      className={cn(
        'h-9 min-w-0 max-w-[14rem] truncate rounded-md border border-input bg-transparent px-3 text-sm',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Champ de recherche de la barre, libellé pour les lecteurs d'écran.
 *
 * `min-w-40` et non `max-w-sm` seul : une fois le retour à la ligne autorisé,
 * un champ sans largeur plancher se réduit à quelques pixels dès qu'il partage
 * sa rangée avec un select.
 */
export function SearchInput({ className, ...props }: React.ComponentProps<typeof Input>) {
  const { t } = useTranslation()
  return (
    <Input
      placeholder={t('common.search')}
      aria-label={t('common.search')}
      className={cn('min-w-40 flex-1 sm:max-w-sm', className)}
      {...props}
    />
  )
}
